import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { LockIcon, SparklesIcon } from 'lucide-react';
import { HiveChestGlyph } from '../../components/icons/BeelyvIcons';
import { HiveGrid } from '../../components/engagement/HiveGrid';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card, SectionTitle } from '../../components/ui/Card';
import { BEE_PER_CELL, engagementConfig, hiveCollectibles, hiveStageFor, hiveStages } from '../../data/engagement';
import { currentUser } from '../../data/user';
import { useEngagement } from '../../contexts/EngagementContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useProgress } from '../../contexts/ProgressContext';
import { buildHistory } from '../../utils/history';

/** MOB/Profile/Hive — Minha Colmeia + Baú da Colmeia. Never touches Life Score. */
export function Hive() {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const progress = useProgress();
  const engagement = useEngagement();
  const reduceMotion = useReducedMotion();
  const historyBee = useMemo(() => buildHistory(90, currentUser.streak).reduce((sum, day) => sum + day.score, 0), []);
  const totalBee = historyBee + progress.dailyXp + engagement.chestBonusBee;
  const {
    stage,
    next
  } = hiveStageFor(totalBee);
  const totalCells = Math.ceil((next ?? stage).minBee / BEE_PER_CELL) || 1;
  const filled = Math.min(totalCells, Math.floor(totalBee / BEE_PER_CELL));
  const chestConfig = engagementConfig.hiveChest;
  const chestReason = engagement.chest ? t(`eng.chest.reason.${engagement.chest.trigger}`, {
    n: chestConfig.streakMilestoneDays
  }) : '';
  return <div className="px-2 pb-2" data-id="element-2825" data-mp="32vavuchjjhtm">
      <PageHeader title={t('eng.hive.title')} subtitle={t('eng.hive.subtitle')} showBack data-id="element-2826" data-mp="32wlvc03ye3ex" />

      <Card tone="dark" className="rounded-panel p-3" data-id="element-2827" data-mp="2oxcinaeyhrfp">
        <p className="bee-overline text-[9px] font-bold text-ondark" data-id="element-2828" data-mp="2sxmvn7161i42">{t('eng.hive.stage')}</p>
        <div className="mt-1 flex items-baseline justify-between gap-2" data-id="element-2829" data-mp="zexilqon54n9">
          <h2 className="text-title text-sand" data-id="element-2830" data-mp="zg8nxuxqikgh">{tl(stage.name)}</h2>
          <p className="bee-num shrink-0 text-[12px] font-medium text-gold" data-id="element-2831" data-mp="un5xhvo97r4i">
            {t('eng.hive.total', {
            n: totalBee.toLocaleString(locale)
          })}
          </p>
        </div>
        <p className="mt-[2px] text-[12.5px] text-ondark" data-id="element-2832" data-mp="kqjezdjehc5n">{tl(stage.description)}</p>

        <div className="mt-3 rounded-card bg-sand px-2 py-2" data-id="element-2833" data-mp="1idjhcywoqjo0">
          <HiveGrid filled={filled} total={totalCells} data-id="element-2834" data-mp="1id0969wu79n6" />
        </div>

        <p className="bee-num mt-2 text-[12px] text-ondark" data-id="element-2835" data-mp="1dg05u6rdgkzr">
          {next ? t('eng.hive.next', {
          n: (next.minBee - totalBee).toLocaleString(locale),
          stage: tl(next.name)
        }) : t('eng.hive.max')}
        </p>
        <p className="mt-[2px] text-[11px] leading-snug text-ondark/80" data-id="element-2836" data-mp="3t636o84famil">
          {t('eng.hive.note', {
          n: BEE_PER_CELL
        })}
        </p>
      </Card>

      <section className="mt-3" aria-labelledby="chest-title" data-id="element-2837" data-mp="3p6c7bg0tyy2g">
        <Card className={`p-2 ${engagement.chest && !engagement.chestOpened ? 'border-gold/50' : ''}`} data-id="element-2838" data-mp="24n8eoeozs4ex">
          <div className="flex items-start gap-2" data-id="element-2839" data-mp="28ollos3tmdnf">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[13px] bg-gold/15 text-gold-deep" data-id="element-2840" data-mp="2dmvit893vobo">
              <HiveChestGlyph size={20} aria-hidden="true" data-id="element-2841" data-mp="2ie564276mfke" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-2842" data-mp="2ncoue99903fx">
              <h2 id="chest-title" className="flex items-center gap-2 text-[14px] font-bold text-deep" data-id="element-2843" data-mp="2nbwwgrs6htmh">
                {t('eng.chest.title')}
                {engagement.chest?.sample && <span className="bee-overline rounded-pill border border-sand-line px-2 py-[3px] text-[8px] font-bold text-muted" data-id="element-2844" data-mp="2wefzbbvys68q">
                    {t('eng.chest.sample')}
                  </span>}
              </h2>
              {engagement.chest ? engagement.chestOpened ? <motion.div initial={reduceMotion ? false : {
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-2845" data-mp="16yylmp76oqzy">
                    <p className="bee-overline mt-1 text-[8.5px] font-bold text-muted" data-id="element-2846" data-mp="lmpq737oejo1">
                      {t('eng.chest.opened')}
                    </p>
                    <p className="bee-num mt-[2px] text-[20px] font-bold text-deep" data-id="element-2847" data-mp="glihn1xplggb">
                      {t('eng.chest.bonus', {
                  n: engagement.chest.bonusBee
                })}
                    </p>
                    <p className="text-[12px] text-muted" data-id="element-2848" data-mp="3k5wjimm7krzw">{chestReason}</p>
                    <p className="mt-[2px] text-[11px] text-gold-deep" data-id="element-2849" data-mp="2odeb9pdqvor">{t('eng.chest.bonusNote')}</p>
                  </motion.div> : <>
                    <p className="mt-[2px] text-[12.5px] text-deep" data-id="element-2850" data-mp="36bvl5tz65702">{t('eng.chest.ready')}</p>
                    <p className="text-[12px] text-muted" data-id="element-2851" data-mp="31e3ut4e69sxz">{chestReason}</p>
                    <Button variant="gold" className="mt-2" onClick={engagement.openChest} data-id="element-2852" data-mp="1gvjjbhk5tjnf">
                      <SparklesIcon size={15} aria-hidden="true" data-id="element-2853" data-mp="1gtq9xui5wlbq" />
                      {t('eng.chest.open')}
                    </Button>
                  </> : <p className="mt-[2px] text-[12px] text-muted" data-id="element-2854" data-mp="16vivbibcifcw">{t('eng.chest.empty')}</p>}
            </div>
          </div>
          <p className="mt-2 border-t border-sand-line pt-2 text-[11px] leading-snug text-muted" data-id="element-2855" data-mp="1zlri5ngttnxt">
            {t('eng.chest.rules')}
            {engagement.chest?.sample && <>
                {' '}
                {t('eng.chest.sampleNote', {
              min: chestConfig.minBonusBee,
              max: chestConfig.maxBonusBee
            })}
              </>}
          </p>
        </Card>
      </section>

      <section className="mt-3" data-id="element-2856" data-mp="f8afvrxuph9m">
        <SectionTitle data-id="element-2857" data-mp="j7j4kie1aoyb">{t('eng.hive.collectibles')}</SectionTitle>
        <ul className="grid grid-cols-2 gap-2" data-id="element-2858" data-mp="j5olzaq8j8xe">
          {hiveCollectibles.map(item => <li key={item.id} className={`flex h-full flex-col rounded-card border p-2 ${item.unlocked ? 'border-gold/40 bg-white/80' : 'border-sand-line bg-sand-deep/40'}`} data-id="element-2859" data-mp="2lwj3n3f2mx0y">
              <p className={`text-[13px] font-bold ${item.unlocked ? 'text-deep' : 'text-muted'}`} data-id="element-2860" data-mp="276buxfev8sdg">
                {tl(item.name)}
              </p>
              <p className="mt-[2px] text-[11px] leading-snug text-muted" data-id="element-2861" data-mp="231vpff95zmyr">{tl(item.rule)}</p>
              <p className={`bee-overline mt-auto flex items-center gap-1 pt-2 text-[8.5px] font-bold ${item.unlocked ? 'text-gold-deep' : 'text-muted'}`} data-id="element-2862" data-mp="1y3kh58yipkin">
                {!item.unlocked && <LockIcon size={10} aria-hidden="true" data-id="element-2863" data-mp="3nkbsijq8o5xj" />}
                {item.unlocked ? t('eng.hive.unlocked') : t('eng.hive.locked')}
              </p>
            </li>)}
        </ul>
      </section>

      <section className="mt-3" data-id="element-2864" data-mp="3r2j1hst3dmj1">
        <SectionTitle data-id="element-2865" data-mp="3r17vdmc6c2or">{t('eng.hive.stages')}</SectionTitle>
        <ol className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2866" data-mp="3v2t2ss5xvork">
          {hiveStages.map(item => {
          const reached = totalBee >= item.minBee;
          const current = item.id === stage.id;
          return <li key={item.id} className="flex items-center gap-2 border-b border-sand-line py-2 last:border-b-0" data-id="element-2867" data-mp="8mynd3ewlfxo">
                <span className={`h-3 w-3 shrink-0 rounded-[4px] ${current ? 'bg-gold' : reached ? 'bg-deep' : 'border border-deep/25'}`} aria-hidden="true" data-id="element-2868" data-mp="3c3p164gq8x9x" />
                <span className={`flex-1 text-[13px] ${current ? 'font-bold text-deep' : reached ? 'text-deep' : 'text-muted'}`} data-id="element-2869" data-mp="3m0jyik19krmm">
                  {tl(item.name)}
                </span>
                <span className="bee-num text-[11.5px] text-muted" data-id="element-2870" data-mp="11aktaiblswnp">
                  {item.minBee.toLocaleString(locale)} Bee
                </span>
              </li>;
        })}
        </ol>
      </section>
    </div>;
}